export type BridgeTenantStatus = 'active' | 'suspended';

/** One brand allowed to send bridge events, as internal-api returns it. */
export interface BridgeTenant {
  tenantId: string;
  brandName: string;
  shopDomain: string | null;
  allowedOrigins: string[];
  hasPublicToken: boolean;
  status: BridgeTenantStatus;
  installedAt: string | null;
  uninstalledAt: string | null;
}

export interface RegisterBridgeTenantInput {
  tenantId: string;
  brandName: string;
  shopDomain?: string;
  allowedOrigins: string[];
}

interface ErrorBody {
  message?: string | string[];
}

/**
 * Talks to the bridge tenant registry on internal-api.
 *
 * Errors are rethrown with the server's own message: registration fails almost entirely on
 * origin validation, and a bare "400" would leave the operator guessing which field was wrong.
 */
export class BridgeTenantClient {
  private static async readError(response: Response): Promise<string> {
    try {
      const body = (await response.json()) as ErrorBody;
      const message = Array.isArray(body.message) ? body.message.join(', ') : body.message;
      if (message) {
        return message;
      }
    } catch {
      // Fall through to the status-only message below.
    }
    return `요청이 실패했습니다 (HTTP ${response.status})`;
  }

  constructor(
    private readonly apiUrl: string,
    private readonly apiKey: string,
  ) {}

  public async list(): Promise<BridgeTenant[]> {
    return this.request<BridgeTenant[]>('/bridgeTenants', { method: 'GET' });
  }

  public async create(input: RegisterBridgeTenantInput): Promise<BridgeTenant> {
    const shopDomain = input.shopDomain?.trim();
    return this.request<BridgeTenant>('/bridgeTenants', {
      method: 'POST',
      body: JSON.stringify({
        tenantId: input.tenantId,
        brandName: input.brandName,
        // An empty string is not a domain; the validator rejects it, so send nothing instead.
        ...(shopDomain ? { shopDomain } : {}),
        allowedOrigins: input.allowedOrigins,
      }),
    });
  }

  public async updateStatus(tenantId: string, status: BridgeTenantStatus): Promise<BridgeTenant> {
    return this.request<BridgeTenant>(`/bridgeTenants/${encodeURIComponent(tenantId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  /** Deletes the brand and, because the event table is interleaved in it, everything it collected. */
  public async remove(tenantId: string): Promise<void> {
    await this.request<void>(`/bridgeTenants/${encodeURIComponent(tenantId)}`, { method: 'DELETE' });
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const response = await fetch(`${this.apiUrl}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', 'X-API-KEY': this.apiKey },
    });

    if (!response.ok) {
      throw new Error(await BridgeTenantClient.readError(response));
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }
}
